import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, of, from } from 'rxjs'
import { map } from 'rxjs/operators'
import { BaseAction } from '../../shared/modules/quick-actions/models/base-action.model'
import { BaseQuickActionProvider } from '../../shared/modules/quick-actions/models/base-quick-action-provider.model'
import { BoReference } from '@lg/shared/for-studio'
import type {
	BranchNameType,
	BusinessObjectIdType,
	BusinessObjectTypeType,
	ModuleIdType,
} from '@lg/shared'
import { DevBarService, StudioTrpcService } from '@lg/ng-shared'

class BONavigateAction extends BaseAction {
	constructor(
		public id: string,
		public label: string,
		private branchName: BranchNameType,
		private moduleId: ModuleIdType,
		private boType: BusinessObjectTypeType,
		private boId: BusinessObjectIdType,
		private devBarService: DevBarService,
	) {
		super({
			icon: 'edit',
			description: `Open ${boType} ${moduleId}.${boId} in editor`,
		})
	}

	execute(): void {
		this.devBarService.openBoEditor(
			this.branchName,
			new BoReference({
				boType: this.boType,
				moduleId: this.moduleId,
				boId: this.boId,
			}),
		)
	}
}

@Injectable({ providedIn: 'root' })
export class EditActionProvider extends BaseQuickActionProvider {
	private router = inject(Router)

	constructor(
		private trpc: StudioTrpcService,
		private devBarService: DevBarService,
	) {
		super('')
	}

	private getRouteParams() {
		let route = this.router.routerState.snapshot.root
		while (route.firstChild) route = route.firstChild

		return {
			branchName: (route.params['branchName'] || 'master') as BranchNameType,
			moduleId: route.params['moduleId'] as ModuleIdType | undefined,
		}
	}

	search(query: string): Observable<BaseAction[]> {
		if (!query) {
			return of([])
		}

		const { branchName, moduleId } = this.getRouteParams()

		return from(
			this.trpc.client.bo.getBosFiltered.query({
				branchName,
				moduleId,
				boTypes: '*',
				query: query || undefined,
			}),
		).pipe(
			map((bos) => {
				const allActions: BaseAction[] = []

				for (const bo of bos) {
					allActions.push(
						new BONavigateAction(
							`${bo.moduleId}.${bo.boId}`,
							`Edit ${bo.__type} ${bo.moduleId}.${bo.boId}`,
							branchName,
							bo.moduleId,
							bo.__type,
							bo.boId,
							this.devBarService,
						),
					)
				}

				return allActions
			}),
		)
	}

	getPrefixHelper(): Observable<string> {
		return of('Search for business objects to edit')
	}
}
